import React, { useMemo, useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Divider, Skeleton, Tabs, DatePicker } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { formatDate } from 'helpers/date';
import HelpTip from 'v2/components/Shared/HelpTip';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import dashboardService from 'v2/services/dashboard';

import Chart from './Chart';

const { RangePicker } = DatePicker;

type TabOption = {
  title: string;
  key: TabKeys;
  isMonetary?: boolean;
  tip?: string;
};

enum TabKeys {
  SALES = 'sales',
  REVENUES = 'revenues',
  GAINS = 'gains'
}

type Period = {
  start: Date;
  end: Date;
};

const tabOptions: ReadonlyArray<TabOption> = [
  { title: 'Vendas', key: TabKeys.SALES, isMonetary: false },
  {
    title: 'Faturamento',
    key: TabKeys.REVENUES,
    isMonetary: true,
    tip: 'Este é o total do seu faturamento bruto; consideramos faturas pagas, aguardando reembolso e reembolsadas.'
  },
  { title: 'Ganhos', key: TabKeys.GAINS, isMonetary: true }
] as const;

const disabledDate = (current: Dayjs) => current.isAfter(dayjs(), 'day');

const LineChart = () => {
  const [period, setPeriod] = useState<Period>({
    start: subDays(startOfDay(new Date()), 7),
    end: new Date()
  });
  const [selectedTab, setSelectedTab] = useState<TabKeys>(TabKeys.GAINS);

  const [isLoading] = useObservable(() => dashboardService.getLoadingPeriodSales(), []);
  const [periodSalesGains] = useObservable(
    () => dashboardService.getPeriodSalesGains(period.start, period.end),
    [period]
  );

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates) return;
    const [start, end] = dates;
    setPeriod({
      start: start ? start.toDate() : period.start,
      end: end ? end.toDate() : period.end
    });
  };

  const totalsByKey = useMemo(() => {
    const sumFor = (key: TabKeys) => {
      const arr = periodSalesGains?.[key] ?? [];
      return arr.reduce((acc, item) => acc + (item.producer ?? 0) + (item.coproducer ?? 0) + (item.affiliate ?? 0), 0);
    };

    return {
      sales: sumFor(TabKeys.SALES),
      revenues: sumFor(TabKeys.REVENUES),
      gains: sumFor(TabKeys.GAINS)
    };
  }, [periodSalesGains]);

  const chartData = useMemo(
    () =>
      (periodSalesGains?.[selectedTab] ?? []).map(item => ({
        ...item,
        date: formatDate(item.date, 'dd/MM')
      })),
    [selectedTab, periodSalesGains]
  );

  const tabItems = tabOptions.map(tabOption => {
    const total = totalsByKey[tabOption.key];
    return {
      key: tabOption.key,
      disabled: isLoading,
      label: (
        <div className='flex min-w-0 flex-col items-start'>
          <span className='flex items-center gap-0.5 text-sm leading-none font-semibold text-shadow-none'>
            {tabOption.title}
            {tabOption.tip && <HelpTip text={tabOption.tip} />}
          </span>
          {isLoading ? (
            <Skeleton.Input active size='small' style={{ width: 80, marginTop: 4 }} />
          ) : tabOption.isMonetary ? (
            <Mask>
              <ColoredFormattedNumber format='BRL' value={total} />
            </Mask>
          ) : (
            <Mask>
              <span>{total}</span>
            </Mask>
          )}
        </div>
      )
    };
  });

  const rangeValue: [Dayjs, Dayjs] = [dayjs(period.start), dayjs(period.end)];

  return (
    <div className='flex h-full min-h-[449px] flex-1 flex-col rounded-sm bg-white shadow-sm'>
      <Tabs
        tabBarStyle={{ marginBottom: 0 }}
        activeKey={selectedTab}
        onChange={key => setSelectedTab(key as TabKeys)}
        type='card'
        style={{ marginBottom: 0 }}
        className='[&_.ant-tabs-nav-list]:w-full [&_.ant-tabs-tab]:flex-1'
        items={tabItems}
      />

      <div className='flex flex-col gap-2 p-3 max-sm:flex-col [&>div]:grow'>
        <label style={{ fontSize: 14 }} htmlFor='start_date_period_sales_gains'>
          Data de pagamento:
        </label>
        <RangePicker
          value={rangeValue}
          onChange={handleDateChange}
          format='DD/MM/YYYY'
          size='middle'
          disabledDate={disabledDate}
          placeholder={['Data inicial', 'Data final']}
          disabled={isLoading}
        />
      </div>
      <Divider className='my-0' />
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
          <Spin />
        </div>
      ) : (
        <Chart data={chartData} totalGains={totalsByKey.gains} />
      )}
    </div>
  );
};

export default LineChart;
